import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import config from '../config/config';
import { authFetch } from '../utils/api';
import Pagination, { DEFAULT_PAGE_SIZE } from './Pagination';
import Skeleton from './common/Skeleton';
import EmptyState from './common/EmptyState';

const BASE_URL = config.BASE_URL;

function severityStyle(severity) {
  switch (severity) {
    case 'HIGH':
      return { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' };
    case 'MEDIUM':
      return { background: '#fffbeb', color: '#d97706', border: '1px solid #fde68a' };
    case 'LOW':
      return { background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' };
    default:
      return { background: '#f8fafc', color: '#64748b', border: '1px solid #cbd5e1' };
  }
}

function SeverityBadge({ severity, score }) {
  const style = severityStyle(severity);
  return (
    <span style={{ ...style, borderRadius: 6, padding: '3px 8px', fontSize: '0.75rem', fontWeight: 700 }}>
      {severity || 'INFO'}{score ? ` • ${score}` : ''}
    </span>
  );
}

function EcosystemBadge({ ecosystem }) {
  const colors = { npm: 'danger', golang: 'info', pypi: 'primary', maven: 'warning', nuget: 'secondary' };
  return (
    <span className={`badge bg-${colors[ecosystem] || 'secondary'} text-white`} style={{ fontSize: '0.65rem' }}>
      {ecosystem || '?'}
    </span>
  );
}

function SummaryCard({ icon, label, value, tone = 'secondary' }) {
  return (
    <div className="ce-stat-card" style={{ '--stat-color': `var(--color-${tone})` }}>
      <div style={{ fontSize: '1rem' }}>{icon}</div>
      <div className="ce-stat-value">{value}</div>
      <div className="ce-stat-label">{label}</div>
    </div>
  );
}

const SEVERITY_COLOR = { CRITICAL: '#dc2626', HIGH: '#ea580c', MEDIUM: '#ca8a04', LOW: '#16a34a' };

function PackageDetailModal({ pkg, onClose, onMarkReviewed, isReviewed }) {
  if (!pkg) return null;
  const pkgKey = pkg.purl || pkg.name;
  const osvUrl = `https://osv.dev/list?q=${encodeURIComponent(pkg.name || pkg.purl || '')}`;

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: 'white', borderRadius: 12, padding: 28, maxWidth: 560, width: '100%', maxHeight: '85vh', overflowY: 'auto', position: 'relative' }}>
        <button
          onClick={onClose}
          style={{ position: 'absolute', top: 12, right: 14, background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: '#64748b' }}
          aria-label="Close"
        >×</button>

        <div className="d-flex align-items-center gap-2 flex-wrap mb-3">
          <EcosystemBadge ecosystem={pkg.ecosystem} />
          <span className="fw-bold fs-6">{pkg.name || pkg.purl}</span>
          {pkg.currentVersion && <code className="small">{pkg.currentVersion}</code>}
        </div>

        {pkg.purl && (
          <div className="small text-secondary mb-3" style={{ wordBreak: 'break-all' }}>
            <strong>PURL:</strong> {pkg.purl}
          </div>
        )}

        <div className="mb-3">
          <SeverityBadge severity={pkg.anomalySeverity} score={pkg.anomalyScore} />
        </div>

        {(pkg.anomalySignals || []).length > 0 && (
          <div className="mb-3">
            <div className="fw-semibold small mb-1">Anomaly Signals</div>
            <ul className="mb-0 ps-3 small">
              {pkg.anomalySignals.map((s) => <li key={s} className="mb-1">{s}</li>)}
            </ul>
          </div>
        )}

        {pkg.anomalySummary && (
          <div className="mb-3 p-3 rounded border small" style={{ background: '#fffaf0' }}>
            <div className="fw-semibold mb-1">Summary</div>
            {pkg.anomalySummary}
          </div>
        )}

        <div className="d-flex flex-wrap gap-3 small text-secondary mb-4">
          {pkg.contributorCount > 0 && <span>👥 Contributors: <strong>{pkg.contributorCount}</strong></span>}
          {pkg.daysSinceLatest >= 0 && <span>🕰️ Days since release: <strong>{pkg.daysSinceLatest}</strong></span>}
          {pkg.maintenanceScore >= 0 && <span>🏆 Maintenance: <strong>{pkg.maintenanceScore}/10</strong></span>}
          {pkg.versionLag > 0 && <span>📦 Version lag: <strong>{pkg.versionLag}</strong></span>}
        </div>

        <div className="d-flex gap-2 flex-wrap mb-4">
          {pkg.depsDevUrl && (
            <a href={pkg.depsDevUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
              🔗 deps.dev
            </a>
          )}
          <a href={osvUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-danger">
            🛡️ OSV Vulnerabilities
          </a>
        </div>

        <button
          className={`btn btn-sm ${isReviewed ? 'btn-success' : 'btn-outline-success'}`}
          onClick={() => { onMarkReviewed(pkgKey); onClose(); }}
          disabled={isReviewed}
        >
          {isReviewed ? '✓ Already Reviewed' : '✓ Mark as Reviewed'}
        </button>
      </div>
    </div>
  );
}

export default function AnomalyDetectionView({ repoName, sbomId }) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [reviewed, setReviewed] = useState(new Set());

  const severity = searchParams.get('anom_severity') || 'all';
  const page = Math.max(1, parseInt(searchParams.get('anom_page') || '1', 10));
  const pageSize = parseInt(localStorage.getItem('depguard_page_size') || String(DEFAULT_PAGE_SIZE), 10);

  const setSeverity = (value) => setSearchParams(prev => {
    const next = new URLSearchParams(prev);
    next.set('anom_severity', value);
    next.set('anom_page', '1');
    return next;
  });

  const setPage = (value) => setSearchParams(prev => {
    const next = new URLSearchParams(prev);
    next.set('anom_page', String(value));
    return next;
  });

  useEffect(() => {
    if (!repoName || !sbomId) return;
    setLoading(true);
    setError('');
    authFetch(`${BASE_URL}/sbom/${repoName}/${sbomId}-sbom.json/health`)
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(setData)
      .catch((e) => setError(e.message || t('anomalies.loadError')))
      .finally(() => setLoading(false));
  }, [repoName, sbomId, t]);

  const anomalies = useMemo(() => {
    const packages = data?.packages || [];
    return packages.filter((pkg) => pkg.isMaintenanceAnomalous);
  }, [data]);

  const filtered = useMemo(() => {
    if (severity === 'all') return anomalies;
    return anomalies.filter((pkg) => (pkg.anomalySeverity || '').toLowerCase() === severity.toLowerCase());
  }, [anomalies, severity]);

  const highCount = anomalies.filter((pkg) => pkg.anomalySeverity === 'HIGH').length;
  const archivedCount = anomalies.filter((pkg) => pkg.isArchived).length;
  const singleMaintainerCount = anomalies.filter((pkg) => pkg.contributorCount === 1).length;
  const avgScore = anomalies.length > 0
    ? (anomalies.reduce((sum, pkg) => sum + (pkg.anomalyScore || 0), 0) / anomalies.length).toFixed(1)
    : '0.0';

  const nPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const chartData = [...anomalies]
    .sort((a, b) => (b.anomalyScore || 0) - (a.anomalyScore || 0))
    .slice(0, 8)
    .map(pkg => ({
      name: (pkg.name || pkg.purl || '?').split('/').pop().substring(0, 12),
      score: pkg.anomalyScore || 0,
      severity: pkg.anomalySeverity || 'UNKNOWN',
      fullName: pkg.name || pkg.purl || '?',
    }));

  const handleMarkReviewed = (pkgKey) => {
    setReviewed(prev => new Set([...prev, pkgKey]));
  };

  if (loading) {
    return (
      <div>
        <Skeleton type="cards" count={4} />
        <Skeleton type="text" count={4} />
      </div>
    );
  }

  if (error) return <div className="alert alert-danger">{t('anomalies.loadError')}: {error}</div>;

  if (anomalies.length === 0) {
    return (
        <EmptyState
          icon="⚠️"
          title={t('anomalies.emptyTitle', 'No maintainer anomalies detected')}
          description={t('anomalies.emptyDescription', 'The current SBOM does not show maintainer or package anomaly signals above the active threshold.')}
        />
      );
  }

  return (
    <div className="d-flex flex-column gap-4">
      <div className="ce-stats-grid">
        <SummaryCard icon="⚠️" label="Flagged packages" value={anomalies.length} tone="warning" />
        <SummaryCard icon="📉" label="Avg anomaly score" value={avgScore} tone="warning" />
        <SummaryCard icon="🗃️" label="Archived repos" value={archivedCount} tone={archivedCount > 0 ? 'danger' : 'secondary'} />
        <SummaryCard icon="👤" label="Single maintainer" value={singleMaintainerCount} tone={singleMaintainerCount > 0 ? 'warning' : 'secondary'} />
        <SummaryCard icon="🚨" label="High severity" value={highCount} tone={highCount > 0 ? 'danger' : 'secondary'} />
      </div>

      {anomalies.length > 0 && (
        <div className="ce-card">
          <div className="ce-card-header">📊 Top Anomalous Packages</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 10]} tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(value, name, props) => [value, props.payload.fullName]}
                labelFormatter={() => ''}
              />
              <Bar dataKey="score" radius={[4, 4, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={index} fill={SEVERITY_COLOR[entry.severity] || '#6366f1'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="ce-card">
        <div className="ce-card-header">⚠️ Maintainer Anomaly Detection</div>
        <div className="mb-3 small text-secondary">
          These findings reuse the package health pipeline and highlight weak maintainer coverage, stale releases, archived repositories, and version lag.
        </div>
        <div className="ce-toolbar">
          <span className="text-secondary small">Severity:</span>
          {['all', 'high', 'medium', 'low'].map((value) => (
            <button
              key={value}
              type="button"
              className={`btn btn-sm ${severity === value ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setSeverity(value)}
            >
              {value === 'all' ? 'All' : value.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div className="d-flex flex-column gap-3">
        {paged.map((pkg) => {
          const pkgKey = pkg.purl || pkg.name;
          const isReviewed = reviewed.has(pkgKey);
          return (
            <div
              key={pkgKey}
              className="ce-card"
              style={{ marginBottom: 0, cursor: 'pointer', position: 'relative' }}
              onClick={() => setSelectedPkg(pkg)}
            >
              {isReviewed && (
                <span style={{
                  position: 'absolute', top: 10, right: 12,
                  background: '#dcfce7', color: '#16a34a', border: '1px solid #bbf7d0',
                  borderRadius: 6, padding: '2px 8px', fontSize: '0.7rem', fontWeight: 700,
                }}>
                  ✓ Reviewed
                </span>
              )}
              <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <EcosystemBadge ecosystem={pkg.ecosystem} />
                  {pkg.depsDevUrl ? (
                    <a
                      href={pkg.depsDevUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-decoration-none fw-semibold"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {pkg.name}
                    </a>
                  ) : (
                    <span className="fw-semibold">{pkg.name}</span>
                  )}
                  <code className="small">{pkg.currentVersion || '—'}</code>
                  <SeverityBadge severity={pkg.anomalySeverity} score={pkg.anomalyScore} />
                </div>

                <div className="d-flex gap-3 flex-wrap small text-secondary">
                  {pkg.contributorCount > 0 && <span>👥 Contributors: {pkg.contributorCount}</span>}
                  {pkg.versionLag > 0 && <span>📦 Version lag: {pkg.versionLag}</span>}
                  {pkg.maintenanceScore >= 0 && <span>🏆 Maintained: {pkg.maintenanceScore}/10</span>}
                  {pkg.daysSinceLatest >= 0 && <span>🕰️ Latest release: {pkg.daysSinceLatest}d ago</span>}
                </div>
              </div>

              {pkg.anomalySummary && (
                <div className="mt-3 p-3 rounded border small" style={{ background: '#fffaf0' }}>
                  {pkg.anomalySummary}
                </div>
              )}

              {(pkg.anomalySignals || []).length > 0 && (
                <ul className="mt-3 mb-0 ps-3 small">
                  {pkg.anomalySignals.map((signal) => (
                    <li key={signal} className="mb-1">{signal}</li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {nPages > 1 && (
        <Pagination
          nPages={nPages}
          currentPage={page}
          setCurrentPage={setPage}
          pageSize={pageSize}
          totalItems={filtered.length}
        />
      )}

      <PackageDetailModal
        pkg={selectedPkg}
        onClose={() => setSelectedPkg(null)}
        onMarkReviewed={handleMarkReviewed}
        isReviewed={selectedPkg ? reviewed.has(selectedPkg.purl || selectedPkg.name) : false}
      />
    </div>
  );
}
